/**
 * Internal app shell for /app/* routes: sidebar, role-based nav (recruiter / AM / admin),
 * logout, user context. Consumes supabase session + user_profile roles.
 * See NAV_GROUPS for section layout; highestRole picks primary nav persona.
 */
import { useState, useEffect, useMemo, createContext, useContext } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Briefcase,
  Users,
  Building2,
  BarChart3,
  CheckCircle,
  FileText,
  Award,
  LogOut,
  PanelLeftOpen,
  PanelLeftClose,
  X,
  MoreHorizontal,
  MessageSquare,
  MessageSquareWarning,
  Diamond,
  Bot,
  Bell,
  Target,
  CalendarDays,
  KeyRound,
} from "lucide-react";
import { useUnreadInboxCount } from "../hooks/useUnreadInboxCount";
import { useNewFeedbackCount } from "../hooks/useNewFeedbackCount";
import { ReactComponent as BlackLogo } from "../styles/Black-SVG-Landscape.svg";

const ROLE_PRIORITY = ["admin", "account_manager", "recruiter", "sales"];

function highestRole(roles) {
  for (const r of ROLE_PRIORITY) {
    if (roles.includes(r)) return r;
  }
  return roles[0] || null;
}

function mergeRolesFromMeta(appMeta) {
  const arr = Array.isArray(appMeta?.roles) ? [...appMeta.roles] : [];
  const legacy = appMeta?.role;
  if (legacy && !arr.includes(legacy)) arr.push(legacy);
  return arr;
}

const UserRoleContext = createContext({
  role: null,
  roles: [],
  rolesResolved: false,
  user: null,
});
export function useUserRole() {
  return useContext(UserRoleContext);
}

const NAV_GROUPS = [
  {
    title: "Recruitment",
    roles: ["admin", "recruiter"],
    items: [
      { label: "Job Board", to: "/app/job-board", icon: Briefcase },
      { label: "Candidates", to: "/app/candidates", icon: Users },
      { label: "Clients", to: "/app/clients", icon: Building2 },
      { label: "Fulfillment", to: "/app/fulfillment", icon: Diamond },
      { label: "My Hirings", to: "/app/my-hirings", icon: Award, roles: ["recruiter"] },
    ],
  },
  {
    title: "Communication",
    roles: ["admin", "recruiter", "account_manager"],
    items: [
      { label: "Inbox", to: "/app/inbox", icon: MessageSquare, inboxBadge: true },
      { label: "Feedbacks", to: "/app/feedback", icon: MessageSquareWarning, feedbackBadge: true, roles: ["admin"] },
    ],
  },
  {
    title: "Acc. Management",
    roles: ["admin", "account_manager"],
    items: [
      { label: "Action Items", to: "/app/action-items", icon: CheckCircle },
      { label: "Reporting", to: "/app/reporting", icon: FileText },
      { label: "Client Portfolio", to: "/app/portfolio", icon: Building2 },
      { label: "My Hirings", to: "/app/my-hirings", icon: Award },
    ],
  },
  {
    title: "Sales Intelligence",
    roles: ["admin", "sales"],
    items: [
      { label: "Pipeline", to: "/app/sales-intelligence/leads", icon: Target },
      { label: "Calendar", to: "/app/sales-intelligence/calendar", icon: CalendarDays },
    ],
  },
  {
    title: "Admin Control",
    roles: ["admin"],
    items: [
      { label: "Business Analytics", to: "/app/analytics", icon: BarChart3 },
      { label: "Notifications", to: "/app/notifications", icon: Bell },
      { label: "OpenClaw", to: "/app/openclaw", icon: Bot },
      { label: "Users", to: "/app/users", icon: Users },
    ],
  },
  {
    title: "Developer",
    roles: ["admin", "api_access"],
    items: [
      { label: "API Access", to: "/app/api-access", icon: KeyRound },
    ],
  },
];

const ROLE_LABELS = {
  admin: "Admin",
  recruiter: "Recruiter",
  account_manager: "Account Manager",
  sales: "Sales",
  api_access: "API Access",
};

const ROLE_COLORS = {
  admin: "bg-indigo-100 text-indigo-700",
  recruiter: "bg-emerald-100 text-emerald-700",
  account_manager: "bg-amber-100 text-amber-700",
  sales: "bg-sky-100 text-sky-700",
  api_access: "bg-slate-100 text-slate-700",
};

function SidebarNavItem({ to, icon: Icon, label, collapsed, badge = 0 }) {
  return (
    <NavLink
      to={to}
      end={to === "/app/job-board"}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-lg py-2.5 text-sm transition-all ${
          collapsed ? "justify-center px-0" : "px-3"
        } ${
          isActive
            ? "bg-indigo-50 font-medium text-gray-900"
            : "font-normal text-gray-500 hover:bg-gray-50 hover:text-gray-700"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-indigo-600" />
          )}
          <Icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? "text-indigo-600" : "text-gray-400 group-hover:text-gray-500"}`} />
          {!collapsed && (
            <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <span className="truncate">{label}</span>
              {badge > 0 && (
                <span className="inline-flex min-w-[18px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1 py-0.5 text-[10px] font-bold leading-none text-white">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const inboxUnread = useUnreadInboxCount();
  const newFeedback = useNewFeedbackCount();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [rolesResolved, setRolesResolved] = useState(false);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    let mounted = true;
    async function loadUser() {
      setRolesResolved(false);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user || !mounted) {
          if (mounted) {
            setUserRoles([]);
            setRolesResolved(true);
          }
          return;
        }
        setUserEmail(session.user.email || "");

        let resolved = mergeRolesFromMeta(session.user.app_metadata);

        const { data: profile } = await supabase
          .from("user_profile")
          .select("role, roles, full_name")
          .eq("id", session.user.id)
          .maybeSingle();

        if (!mounted) return;
        if (profile) {
          const dbRoles = Array.isArray(profile.roles) && profile.roles.length > 0
            ? profile.roles
            : profile.role ? [profile.role] : [];
          dbRoles.forEach((r) => { if (!resolved.includes(r)) resolved.push(r); });
          if (profile.full_name) setUserName(profile.full_name);
        }

        if (resolved.length === 0) {
          const { data: adminRow } = await supabase
            .from("admins")
            .select("id, first_name, last_name")
            .eq("id", session.user.id)
            .maybeSingle();
          if (!mounted) return;
          if (adminRow) {
            resolved.push("admin");
            if (!profile?.full_name && (adminRow.first_name || adminRow.last_name)) {
              setUserName(`${adminRow.first_name || ""} ${adminRow.last_name || ""}`.trim());
            }
          }
        }

        setUserRoles(resolved);
      } finally {
        if (mounted) setRolesResolved(true);
      }
    }
    loadUser();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const displayRole = highestRole(userRoles);

  const filteredGroups = useMemo(() => {
    if (userRoles.length === 0) return [];
    return NAV_GROUPS
      .filter((g) => g.roles.some((r) => userRoles.includes(r)))
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => !item.roles || item.roles.some((r) => userRoles.includes(r))),
      }))
      .filter((g) => g.items.length > 0);
  }, [userRoles]);

  // Bottom bar: first 3 nav links that are not Inbox (Inbox via "More" / sidebar); no unread badge here.
  const mobileBottomItems = useMemo(() => {
    const all = filteredGroups.flatMap((g) => g.items);
    const withoutInbox = all.filter((i) => i.to !== "/app/inbox");
    return withoutInbox.slice(0, 3);
  }, [filteredGroups]);

  const isActive = (to) => {
    if (to === "/app/job-board") return location.pathname === "/app/job-board";
    return location.pathname.startsWith(to);
  };

  const initials = userName
    ? userName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : userEmail ? userEmail[0].toUpperCase() : "?";

  const ctxValue = useMemo(
    () => ({
      role: displayRole,
      roles: userRoles,
      rolesResolved,
      user: { email: userEmail, name: userName },
    }),
    [displayRole, userRoles, rolesResolved, userEmail, userName],
  );

  return (
    <UserRoleContext.Provider value={ctxValue}>
      <div className="flex h-screen overflow-hidden bg-gray-50">
        {/* ═══ DESKTOP SIDEBAR ═══ */}
        <aside
          className="relative z-10 hidden h-full shrink-0 flex-col overflow-visible border-r border-gray-200 bg-white transition-all duration-200 md:flex"
          style={{ width: collapsed ? 68 : 240 }}
        >
          {/* Logo + collapse — when collapsed, toggle sits on the outer edge (half outside the bar) */}
          <div
            className={`relative border-b border-gray-100 px-3 py-3 ${
              collapsed ? "flex items-center justify-center" : "flex items-center justify-between gap-2"
            }`}
          >
            {collapsed ? (
              <img
                src="/logo1921.png"
                alt="AI Architechs"
                className="h-8 w-8 shrink-0 rounded-lg object-cover"
                width={32}
                height={32}
              />
            ) : (
              <BlackLogo className="h-8 w-auto min-w-0 max-w-[calc(100%-2.5rem)]" />
            )}
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className={
                collapsed
                  ? "absolute left-full top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-2 text-gray-500 shadow-md transition-colors hover:bg-gray-50 hover:text-gray-700"
                  : "shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
              }
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand" : "Collapse"}
            >
              {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
            </button>
          </div>

          {/* User card */}
          <div className={`flex items-center gap-3 border-b border-gray-100 px-4 py-3 ${collapsed ? "justify-center" : ""}`}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
              {initials}
            </div>
            {!collapsed && (
              <div className="flex min-w-0 flex-1 flex-col gap-0">
                <p className="truncate text-sm font-medium leading-tight text-gray-900">{userName || userEmail}</p>
                {displayRole && (
                  <span className={`inline-block w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none ${ROLE_COLORS[displayRole] || "bg-gray-100 text-gray-600"}`}>
                    {ROLE_LABELS[displayRole] || displayRole}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Nav groups */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            {filteredGroups.map((group, gi) => (
              <div key={group.title} className={gi > 0 ? "mt-5" : ""}>
                {collapsed ? (
                  gi > 0 && <div className="mx-2 mb-3 h-px bg-gray-100" />
                ) : (
                  <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[1.5px] text-gray-400">
                    {group.title}
                  </p>
                )}
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <SidebarNavItem
                      key={item.to}
                      to={item.to}
                      icon={item.icon}
                      label={item.label}
                      collapsed={collapsed}
                      badge={
                        item.inboxBadge
                          ? inboxUnread
                          : item.feedbackBadge
                            ? newFeedback
                            : 0
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Bottom: sign out */}
          <div className="border-t border-gray-100 px-3 py-3">
            <button
              onClick={handleLogout}
              className={`flex w-full items-center gap-3 rounded-lg py-2.5 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 ${collapsed ? "justify-center px-0" : "px-3"}`}
            >
              <LogOut className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span>Sign Out</span>}
            </button>
          </div>
        </aside>

        {/* ═══ MAIN CONTENT ═══ */}
        <main className="flex flex-1 min-h-0 flex-col overflow-y-auto pb-[72px] md:pb-0">
          <Outlet />
        </main>

        {/* ═══ MOBILE BOTTOM NAV ═══ */}
        <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-gray-200 bg-white px-2 md:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)", height: 64 }}
        >
          {mobileBottomItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={`relative flex flex-col items-center gap-0.5 rounded-lg px-3 py-1.5 text-[10px] font-medium transition-colors ${
                  active ? "text-indigo-600" : "text-gray-400"
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? "text-indigo-600" : "text-gray-400"}`} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="flex flex-col items-center gap-0.5 rounded-lg px-3 py-1.5 text-[10px] font-medium text-gray-400"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span>More</span>
          </button>
        </div>

        {/* ═══ MOBILE FULL-SCREEN OVERLAY ═══ */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 flex flex-col bg-white md:hidden animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <BlackLogo className="h-8 w-auto" />
              <button onClick={() => setMobileMenuOpen(false)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-50">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* User card */}
            <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                {initials}
              </div>
              <div className="flex min-w-0 flex-col gap-0">
                <p className="truncate text-sm font-medium leading-tight text-gray-900">{userName || userEmail}</p>
                {displayRole && (
                  <span className={`inline-block w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none ${ROLE_COLORS[displayRole] || "bg-gray-100 text-gray-600"}`}>
                    {ROLE_LABELS[displayRole] || displayRole}
                  </span>
                )}
              </div>
            </div>

            {/* Nav items */}
            <nav className="flex-1 overflow-y-auto px-4 py-4">
              {filteredGroups.map((group, gi) => (
                <div key={group.title} className={gi > 0 ? "mt-5" : ""}>
                  <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[1.5px] text-gray-400">
                    {group.title}
                  </p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.to);
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={`relative flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all ${
                            active
                              ? "bg-indigo-50 font-medium text-gray-900"
                              : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                          }`}
                        >
                          {active && (
                            <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-indigo-600" />
                          )}
                          <Icon className={`h-[18px] w-[18px] ${active ? "text-indigo-600" : "text-gray-400"}`} />
                          <span>{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Sign out */}
            <div className="border-t border-gray-100 px-4 py-4" style={{ paddingBottom: "env(safe-area-inset-bottom, 16px)" }}>
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
              >
                <LogOut className="h-[18px] w-[18px]" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </UserRoleContext.Provider>
  );
}
